import shaka from 'shaka-player';
import { canKeyboardSeek, clampSeekTime } from './keyboardControls';
import { createRegisteredShakaIconElement, ensureDefaultShakaIconsRegistered } from './shakaIconRegistry';

const CUSTOM_REWIND_CONTROL = 'rewind10';
const CUSTOM_FORWARD_CONTROL = 'forward10';
const SETTINGS_ALIAS_CONTROL = 'settings';
const SEEK_STEP_SECONDS = 10;

type ShakaElementBase = shaka.ui.Element & {
  parent: HTMLElement;
  controls: shaka.ui.Controls;
  player: shaka.Player;
  video: HTMLVideoElement;
  localization: shaka.ui.Localization;
  eventManager: {
    listen: (target: EventTarget, type: string, listener: EventListenerOrEventListenerObject) => void;
    release: () => void;
  };
};

type ShakaElementCtor = new (parent: HTMLElement, controls: shaka.ui.Controls) => ShakaElementBase;
const ShakaElementBaseCtor = shaka.ui.Element as unknown as ShakaElementCtor;

function resolveAccessibilityLabel(localization: shaka.ui.Localization, fallbackKey: 'REWIND' | 'FAST_FORWARD'): string {
  const base = localization.resolve(fallbackKey);
  return `${base} ${SEEK_STEP_SECONDS} seconds`;
}

class Rewind10Button extends ShakaElementBaseCtor {
  private readonly button: HTMLButtonElement;

  constructor(parent: HTMLElement, controls: shaka.ui.Controls) {
    super(parent, controls);
    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.classList.add('shaka-rewind10-button', 'shaka-no-propagation', 'shaka-tooltip');
    this.button.appendChild(createRegisteredShakaIconElement(CUSTOM_REWIND_CONTROL));
    this.parent.appendChild(this.button);

    this.eventManager.listen(this.button, 'click', () => this.onSeek());
    this.eventManager.listen(this.localization, 'locale-updated', () => this.updateAriaLabel());
    this.eventManager.listen(this.localization, 'locale-changed', () => this.updateAriaLabel());
    this.eventManager.listen(this.video, 'timeupdate', () => this.syncDisabledState());

    this.updateAriaLabel();
    this.syncDisabledState();
  }

  private onSeek(): void {
    const treatAsLive = this.player.isLive();
    if (!canKeyboardSeek(this.video, treatAsLive)) return;
    this.video.currentTime = clampSeekTime(this.video, treatAsLive, this.video.currentTime - SEEK_STEP_SECONDS);
  }

  private updateAriaLabel(): void {
    this.button.ariaLabel = resolveAccessibilityLabel(this.localization, 'REWIND');
  }

  private syncDisabledState(): void {
    const treatAsLive = this.player.isLive();
    this.button.disabled = !canKeyboardSeek(this.video, treatAsLive);
  }

  override release(): void {
    this.button.remove();
    super.release();
  }
}

class Forward10Button extends ShakaElementBaseCtor {
  private readonly button: HTMLButtonElement;

  constructor(parent: HTMLElement, controls: shaka.ui.Controls) {
    super(parent, controls);
    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.classList.add('shaka-forward10-button', 'shaka-no-propagation', 'shaka-tooltip');
    this.button.appendChild(createRegisteredShakaIconElement(CUSTOM_FORWARD_CONTROL));
    this.parent.appendChild(this.button);

    this.eventManager.listen(this.button, 'click', () => this.onSeek());
    this.eventManager.listen(this.localization, 'locale-updated', () => this.updateAriaLabel());
    this.eventManager.listen(this.localization, 'locale-changed', () => this.updateAriaLabel());
    this.eventManager.listen(this.video, 'timeupdate', () => this.syncDisabledState());

    this.updateAriaLabel();
    this.syncDisabledState();
  }

  private onSeek(): void {
    const treatAsLive = this.player.isLive();
    if (!canKeyboardSeek(this.video, treatAsLive)) return;
    this.video.currentTime = clampSeekTime(this.video, treatAsLive, this.video.currentTime + SEEK_STEP_SECONDS);
  }

  private updateAriaLabel(): void {
    this.button.ariaLabel = resolveAccessibilityLabel(this.localization, 'FAST_FORWARD');
  }

  private syncDisabledState(): void {
    const treatAsLive = this.player.isLive();
    this.button.disabled = !canKeyboardSeek(this.video, treatAsLive);
  }

  override release(): void {
    this.button.remove();
    super.release();
  }
}

class Rewind10Factory implements shaka.extern.IUIElement.Factory {
  create(rootElement: HTMLElement, controls: shaka.ui.Controls): shaka.extern.IUIElement {
    return new Rewind10Button(rootElement, controls);
  }
}

class Forward10Factory implements shaka.extern.IUIElement.Factory {
  create(rootElement: HTMLElement, controls: shaka.ui.Controls): shaka.extern.IUIElement {
    return new Forward10Button(rootElement, controls);
  }
}

class SettingsAliasFactory implements shaka.extern.IUIElement.Factory {
  create(rootElement: HTMLElement, controls: shaka.ui.Controls): shaka.extern.IUIElement {
    const uiNamespace = shaka.ui as unknown as { ResolutionSelection?: ShakaElementCtor };
    const ResolutionSelection = uiNamespace.ResolutionSelection;
    if (ResolutionSelection) {
      return new ResolutionSelection(rootElement, controls);
    }
    // Fallback: preserve stability if ResolutionSelection is unavailable in a given bundle.
    return new ShakaElementBaseCtor(rootElement, controls);
  }
}

let controlsRegistered = false;

export function ensureShakaCustomSeekControlsRegistered(): void {
  if (controlsRegistered) return;
  controlsRegistered = true;
  ensureDefaultShakaIconsRegistered();

  shaka.ui.Controls.registerElement(CUSTOM_REWIND_CONTROL, new Rewind10Factory());
  shaka.ui.Controls.registerElement(CUSTOM_FORWARD_CONTROL, new Forward10Factory());
  shaka.ui.Controls.registerElement(SETTINGS_ALIAS_CONTROL, new SettingsAliasFactory());
}
